import { useState } from "react";
import { X, Link2, Send } from "lucide-react";
import AppSelect from "shared/AppSelect";
import { fetchJobByUrl, fetchJobByUrlAsEmployee, type TechManager } from "../api/LinkedInJobsApi";

interface Props {
  isAdmin: boolean;
  techManagers: TechManager[];
  onClose: () => void;
}

export default function AddJobByUrlModal({ isAdmin, techManagers, onClose }: Props) {
  const [url, setUrl] = useState("");
  const [employeeCode, setEmployeeCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!url.trim()) {
      setError("Paste a LinkedIn job URL first.");
      return;
    }
    if (isAdmin && !employeeCode) {
      setError("Choose which Technical Manager this job should be assigned to.");
      return;
    }

    setSubmitting(true);
    try {
      if (isAdmin) {
        await fetchJobByUrl(url.trim(), employeeCode);
      } else {
        await fetchJobByUrlAsEmployee(url.trim());
      }
      setSubmitted(true);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong submitting this job.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-md p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center">
              <Link2 className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold text-foreground font-inter">Add LinkedIn Job by URL</h2>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {submitted ? (
          <div className="text-center py-4">
            <p className="text-sm text-foreground font-inter font-medium mb-1.5">Job submitted</p>
            <p className="text-xs text-muted-foreground font-inter">
              It's being scraped and analyzed now. It'll appear on the Job Board only if it
              passes the same remote-scope and company-fit checks every other job goes
              through — if it doesn't qualify, it won't show up here.
            </p>
            <button
              onClick={onClose}
              className="mt-5 px-4 py-2 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 rounded-xl transition-colors font-inter"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <p className="text-xs text-muted-foreground font-inter mb-4">
              Paste a LinkedIn job posting URL — it goes through the exact same scraping and
              remote-scope/company-fit analysis as email-alert jobs, and only lands on the
              Job Board if it qualifies.
              {isAdmin
                ? " If it does, it'll be assigned to the Technical Manager you pick below."
                : " If it does, it'll be assigned to you."}
            </p>

            <div className={isAdmin ? "mb-4" : "mb-5"}>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5 font-inter">Job URL</label>
              <input
                autoFocus
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.linkedin.com/jobs/view/1234567890/"
                className="w-full bg-surface-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary font-inter"
              />
            </div>

            {isAdmin && (
              <div className="mb-5">
                <label className="block text-xs font-medium text-muted-foreground mb-1.5 font-inter">Assign to</label>
                <AppSelect
                  value={employeeCode}
                  onChange={setEmployeeCode}
                >
                  <option value="">Select a Technical Manager…</option>
                  {techManagers.map((e) => (
                    <option key={e.employee_code} value={e.employee_code}>{e.name}</option>
                  ))}
                </AppSelect>
              </div>
            )}

            {error && (
              <p className="text-xs text-destructive font-inter mb-4 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-xl transition-colors disabled:opacity-40 font-inter"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-inter"
              >
                <Send className="w-4 h-4" />
                {submitting ? "Submitting…" : "Submit"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
